import { useState, useMemo, memo } from 'react'
import { Link, useMatchRoute } from '@tanstack/react-router'
import {
  Home,
  Users,
  BarChart3,
  Clock,
  Layers,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  LogOut,
  User,
  Target,
  Settings,
  Wallet,
  Receipt,
  Package,
  Image as ImageIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSidebarStore } from '@/stores/sidebar-store'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LogoutDialog } from '@/features/auth/components/logout-dialog'
interface NavItem {
  label: string
  href: string
  icon: typeof Home
  /** Identificador do menu para controle de acesso (campo 'acessos' da permissao) */
  menuKey: string
}

const navItems: NavItem[] = [
  { label: 'Home', href: '/home', icon: Home, menuKey: 'home' },
  { label: 'Financeiro', href: '/financeiro', icon: DollarSign, menuKey: 'financeiro' },
  { label: 'Clientes', href: '/clientes', icon: Users, menuKey: 'clientes' },
  { label: 'Relatório Vendas', href: '/relatorio-vendas', icon: BarChart3, menuKey: 'relatorio-vendas' },
  { label: 'Dados Históricos', href: '/dados-historicos', icon: Clock, menuKey: 'dados-historicos' },
  { label: 'Grupos', href: '/grupos', icon: Layers, menuKey: 'grupos' },
  { label: 'Metas Loja', href: '/metas-loja', icon: Target, menuKey: 'metas-loja' },
  { label: 'Metas Individuais', href: '/metas-individuais', icon: Users, menuKey: 'metas-individuais' },
  { label: 'Fluxo de Caixa', href: '/fluxo-caixa', icon: Wallet, menuKey: 'fluxo-caixa' },
  { label: 'DRE', href: '/dre', icon: Receipt, menuKey: 'dre' },
  { label: 'Estoque', href: '/estoque', icon: Package, menuKey: 'estoque' },
  { label: 'Fotos', href: '/fotos', icon: ImageIcon, menuKey: 'fotos' },
  { label: 'Configurações', href: '/configuracoes', icon: Settings, menuKey: 'configuracoes' },
]

const roleLabels: Record<string, string> = {
  admin: 'Administrador',
  vendedor: 'Vendedor',
  gerencia: 'Gerência',
  contabilidade: 'Contabilidade',
}

/** Retorna o nome da permissão do usuário (prioriza dados do join com tabela permissao) */
function getPermissaoLabel(profile: { permissao?: { permissao: string | null } | null; role?: string } | null): string {
  if (profile?.permissao?.permissao) {
    const perm = profile.permissao.permissao
    return perm.charAt(0).toUpperCase() + perm.slice(1)
  }
  if (profile?.role) {
    return roleLabels[profile.role] || profile.role
  }
  return 'Carregando...'
}

const LOGO_URL = 'https://srwzlazgkldflwbcpgwm.supabase.co/storage/v1/object/public/geral/logo1_marrom_sem_fundo.png'
const FAVICON_URL = 'https://srwzlazgkldflwbcpgwm.supabase.co/storage/v1/object/public/geral/favicom.png'

// Memoized nav item component para evitar re-renders
const NavItem = memo(function NavItem({
  item,
  isActive,
  isExpanded,
}: {
  item: NavItem
  isActive: boolean
  isExpanded: boolean
}) {
  const Icon = item.icon
  return (
    <Link
      to={item.href}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-100',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        !isExpanded && 'justify-center px-2'
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {isExpanded && <span className="truncate">{item.label}</span>}
    </Link>
  )
})

function getInitials(name: string | null | undefined): string {
  if (!name) return 'U'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function Sidebar() {
  const { isExpanded, toggle } = useSidebarStore()
  const { profile, hasMenuAccess } = useAuth()
  const matchRoute = useMatchRoute()
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)

  // Memoiza filtro de itens para evitar recálculo a cada render
  const filteredNavItems = useMemo(
    () => navItems.filter((item) => hasMenuAccess(item.menuKey)),
    [hasMenuAccess]
  )

  return (
    <TooltipProvider delayDuration={100}>
      <aside
        style={{ width: isExpanded ? 256 : 64, minWidth: isExpanded ? 256 : 64 }}
        className="fixed left-0 top-0 z-40 hidden h-screen border-r bg-card md:flex md:flex-col transition-[width] duration-200 ease-out"
      >
        {/* Logo */}
        <div className={cn(
          "flex h-16 items-center border-b px-4",
          isExpanded ? "justify-between" : "justify-center"
        )}>
          {isExpanded ? (
            <Link to="/home" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Bárbara Monteiro" className="h-10 object-contain" />
            </Link>
          ) : (
            <Link to="/home" className="flex items-center justify-center">
              <img src={FAVICON_URL} alt="BM" className="h-8 w-8 object-contain" />
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label={isExpanded ? 'Retrair menu' : 'Expandir menu'}
            className={cn(
              "shrink-0",
              !isExpanded &&
                "absolute right-0 top-16 h-5 w-5 -translate-y-1/2 translate-x-1/2 rounded-full border bg-background shadow-sm hover:bg-background"
            )}
          >
            {isExpanded ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </Button>
        </div>

        {/* Navigation */}
        <nav className={cn(
          "flex-1 overflow-y-auto overflow-x-hidden py-4",
          isExpanded ? "px-2" : "px-1"
        )}>
          <ul className="space-y-1">
            {filteredNavItems.map((item) => {
              const isActive = !!matchRoute({ to: item.href, fuzzy: true })

              if (!isExpanded) {
                return (
                  <li key={item.href}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <NavItem item={item} isActive={isActive} isExpanded={false} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  </li>
                )
              }

              return (
                <li key={item.href}>
                  <NavItem item={item} isActive={isActive} isExpanded={true} />
                </li>
              )
            })}
          </ul>
        </nav>

        {/* User Profile Footer */}
        <div className="border-t p-3">
          {isExpanded ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3 px-3">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={undefined} alt={profile?.nome ?? ''} />
                    <AvatarFallback>{getInitials(profile?.nome)}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-left overflow-hidden">
                    <span className="text-sm font-medium truncate w-full">{profile?.nome ?? 'Usuário'}</span>
                    <Badge variant="secondary" className="text-xs mt-0.5">
                      {getPermissaoLabel(profile)}
                    </Badge>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start" side="top">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{profile?.nome}</p>
                    <p className="text-xs leading-none text-muted-foreground">{profile?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/perfil" className="flex w-full items-center">
                    <User className="mr-2 h-4 w-4" />
                    <span>Perfil</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => { setShowLogoutDialog(true); }}
                  className="text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-full">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={undefined} alt={profile?.nome ?? ''} />
                        <AvatarFallback className="text-xs">{getInitials(profile?.nome)}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="start" side="right">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{profile?.nome}</p>
                        <p className="text-xs leading-none text-muted-foreground">{profile?.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/perfil" className="flex w-full items-center">
                        <User className="mr-2 h-4 w-4" />
                        <span>Perfil</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => { setShowLogoutDialog(true); }}
                      className="text-destructive"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Sair</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TooltipTrigger>
              <TooltipContent side="right">Perfil</TooltipContent>
            </Tooltip>
          )}
        </div>
      </aside>

      <LogoutDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog} />
    </TooltipProvider>
  )
}
